import React from 'react';
import { compose } from 'ramda';

import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { Grid, Carousel } from '_utils/components/Corporate';
import Section, { BackgroundColors } from '_utils/components/Corporate/Section';
import HeadingWithDescription from '_utils/components/Corporate/HeadingWithDescription';

import { RelatedContentProps } from './definitions';

import { CarouselItem, ItemsListContainer } from './StyledRelatedContent';

import RelatedItem from './RelatedItem';
import PropTypes from 'prop-types';

const EmptyList = compose(withEditModeNotice)(Carousel);
const RelatedItems = ({ editMode, relatedItems, id }) => {
  if (!editMode) {
    return (
      <Carousel key={id} flickityOptions={{ groupCells: true, cellAlign: 'left' }}>
        {relatedItems.map((props, index) => (
            <CarouselItem key={`${id}_${index}`}>
              <RelatedItem {...props} editMode={editMode} />
            </CarouselItem>
          ))}
      </Carousel>
    );
  }

  return (
    <ItemsListContainer>
      {relatedItems.map((props, index) => (
        <CarouselItem key={`${id}_${index}`}>
          <RelatedItem {...props} editMode={editMode} />
        </CarouselItem>
      ))}
    </ItemsListContainer>
  );
};

RelatedItems.propTypes = {
  editMode: PropTypes.bool.isRequired,
  relatedItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  id: PropTypes.string.isRequired,
};

const RelatedContent: React.FC<RelatedContentProps> = ({ rendering, editMode, fields }) => {
  if (fields?.data?.datasource === null) {
    if (editMode) {
      return (
        <Section sectionTheme={BackgroundColors['Light beige']} className="hide-on-print">
          <Grid.Container>
            <EmptyList noticeEnabled={true} noticeMessage={`Carousel has no datasource`} />
          </Grid.Container>
        </Section>
      );
    }

    return null;
  }

  const {
    items,
    heading: { jss: heading },
    headingLevel: { jss: headingLevel },
    description: { jss: description }
  } = fields.data.datasource;

  const validItems =
    // add valid key for use down the pipe
    items && items.length
      ? items
          .map((item) => ({
            ...item,
            valid: item.title !== null && item.link !== null
          }))
          .filter(({ valid }) => editMode || valid)
      : [];

  if (!editMode && validItems.length === 0) {
    return null;
  }

  const id = rendering?.dataSource || '' + Math.random();

  return (
    <Section sectionTheme={BackgroundColors['Light beige']} className="hide-on-print">
      <Grid.Container>
        {Boolean(heading || description) && (
          <HeadingWithDescription
            headingField={heading}
            descriptionField={description}
            headingLevel={headingLevel}
          />
        )}
        {validItems.length > 0 ? (
          <RelatedItems relatedItems={validItems} editMode={editMode} id={id} />
        ) : (
          <EmptyList noticeEnabled={true} noticeMessage={`Carousel contains no items`} />
        )}
      </Grid.Container>
    </Section>
  );
};

export default compose(withEditMode)(RelatedContent);
